import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Util, UserInterface } from './utils/utils.module';
import { LocalizeService } from './services/localize.service';
import { FormWrapperComponent } from './forms/form-wrapper.component';
import { DialogKind } from './models/form-field';
import { ShortcutsService } from './services/shortcuts.service';

enum ToggleState { none = 0, startclose = 1, finishclose = 2, startopen = 3, justclose = 4 }

@Component({
  selector: 'edx-app-notify-dialog',
  styleUrls: ['app-notify-dialog-component.scss'],
  template: `
  <div #toggleStateBtn class="togglestatebtn" (click)="doToggleState($event)"></div>
  @if (blocking && (shown || isShowing || isHiding)) {
    <div class="container-mask" [ngClass]="{edx_hidden:hidden}"></div>
  }
  <div class="container" [ngClass]="{edx_hidden:hidden,mobile:ui>1,shown:shown,showing:isShowing,hiding:isHiding,success:kind===1,info:kind===2,warning:kind===3,progress:kind===4,error:kind===5,confirm:kind===6}" (animationend)="notifyAnimationComplete()"
    role="dialog" aria-modal="true" [attr.aria-label]="kindText" [attr.aria-labelledby]="!isChrome?'edx_notify_body':null">
    <div class="header" #dialogHeader [ngClass]="{subform:subFormData, oai:isAddin}" id="edx_notify_header" tabindex="30" [attr.aria-label]="title+' '+dialogLabel" role="heading" (keydown.shift.tab)="loopFocus($event)">
      <div class="icon">
        @if (kind===4) {
          <edx-spinner [mini]="true"></edx-spinner>
        }
      </div>
      <div class="title" [ngClass]="{progress:kind===4}">{{title}}</div>
      @if (!showConfirm()) {
        <div class="close" role="button" [title]="close" tabindex="31" (click)="doClose($event)" (keyup.enter)="doClose($event)" (keyup.space)="doClose($event)" id="edx_notify_btn_close"></div>
      }
    </div>
    @if (lines) {
      <div class="body" id="edx_notify_body" (keydown.tab)="!showConfirm()?loopFocus($event):null">
        @for (line of lines; track line) {
          <div class="line" [attr.role]="!lineIsLink(line)?'note':'none'" [attr.aria-label]="!lineIsLink(line)?lines:null">
            @if (lineIsLink(line)) {
              <span>
                <br>
                  <a target="_blank" [href]="getLinkFromLine(line)" tabindex="32" role="link" (click)="openLink(getLinkFromLine(line), getLinkTitleFromLine(line), $event)" (keyup.enter)="openLink(getLinkFromLine(line), getLinkTitleFromLine(line), $event)" (keyup.space)="openLink(getLinkFromLine(line), getLinkTitleFromLine(line), $event)">{{getLinkTitleFromLine(line)}}</a>
                </span>
              }
              @if (!lineIsLink(line)) {
                <span>{{line}}</span>
              }
            </div>
          }
        </div>
      }
      @if (subFormName) {
        <edx-form-wrapper #subFormWrapper [tabIndex]="33" [desc]="null" [kind]="subFormName" [inNotify]="true" [selections]="null" [formData]="subFormData"></edx-form-wrapper>
      }
      @if (showConfirm()) {
        <div class="buttons">
          @if (cancelBtn) {
            <button title={{cancel}} role="button" [ngClass]="{mobile:ui>=2, oai:isAddin, secondary:kind!==3, primary:kind===3}" tabindex="35" (click)="confirmCancel()" (keyup.enter)="confirmCancel()" (keyup.space)="confirmCancel()" id="edx_notify_btn_cancel" (keydown.tab)="loopFocus($event)">{{cancel}}</button>
          }
          @if (oKBtn) {
            <button title={{ok}} role="button" [ngClass]="{mobile:ui>=2, oai:isAddin, primary:kind!==3, secondary:kind===3, readonly:(subFormWrapper && subFormWrapper.okDisabled)}" tabindex="34" (click)="confirmOK()" (keyup.enter)="confirmOK()" (keyup.space)="confirmOK()" [disabled]="subFormWrapper && subFormWrapper.okDisabled" id="edx_notify_btn_ok">{{ok}}</button>
          }
        </div>
      }
    </div>
  `,
  standalone: false
})
export class AppNotifyDialogComponent implements OnInit, OnDestroy {
  @ViewChild('subFormWrapper') private subFormWrapper: FormWrapperComponent;
  @ViewChild('toggleStateBtn') private toggleStateBtn: ElementRef;
  @ViewChild('dialogHeader') private dialogHeader: ElementRef;
  public shown = false;
  public isShowing = false;
  public isHiding = false;
  public blocking = false;
  public hidden: boolean;  // this is temporary
  public title = '';
  public lines: string[] = null;
  public subFormData: any = {};
  public kind: DialogKind = DialogKind.none;
  public kindText: string;
  public ui: UserInterface;
  public isAddin: boolean;
  public subFormName: string = null;
  private togglingCloseOpen: ToggleState = ToggleState.none;
  private ok = '';
  private cancel = '';
  private close = '';
  private oKBtn = false;
  private cancelBtn = false;
  private userConfirmed: boolean = undefined;
  private isDynamicComponent = false;
  public dialogLabel: string;
  public isChrome = false;

  constructor(private localizer: LocalizeService, private shortcutService: ShortcutsService) {
    this.ui = Util.Device.ui;
    this.isAddin = Util.Device.bIsAddin;
    this.dialogLabel = this.localizer.getTranslation('ALT_TEXT.DIALOG');
    this.isChrome = Util.Device.bIsChrome;
  }

  @HostListener('document:keyup', ['$event'])
  keyUp(event: KeyboardEvent) {
    if (this.shown) {
      if (event.key === 'Enter') {
        const currentEl = event.target as HTMLElement;
        if (!!currentEl && ['edx_notify_btn_cancel', 'edx_notify_btn_close'].indexOf(currentEl.id) === -1) {
          this.confirmOK();
        }
      } else if (event.key === 'Escape') {
        this.confirmCancel();
      } else if (event.key === 'ArrowUp') {
        if (this.ui >= 2 && !this.isAddin) {
          if (++this.kind > 6) {
            this.kind = 1;
            this.updateKindText();
          }
        }
      }
    }
  }

  ngOnInit() {
    Util.Notify.setAppNotifyDialog(this);
    this.ok = this.localizer.getTranslation('FORMS.BUTTONS.OK');
    this.cancel = this.localizer.getTranslation('FORMS.BUTTONS.CANCEL');
    this.close = this.localizer.getTranslation('FORMS.BUTTONS.CLOSE');
  }

  ngOnDestroy() {
    Util.Notify.setAppNotifyDialog(null);
    this.shortcutService.setDisableShortcuts(false);
  }

  public loopFocus(event: KeyboardEvent): void {
    if (!!event) {
      event.preventDefault();
      setTimeout(() => {
        const element = (event.shiftKey && this.showConfirm() ? document.getElementById('edx_notify_btn_cancel') : (this.dialogHeader.nativeElement === document.activeElement) ? document.getElementById('edx_notify_body') : this.dialogHeader.nativeElement) as HTMLElement;
        if (!!element) {
          element.focus();
        }
      }, 1);
    }
  }

  public showConfirm(): boolean {
    return this.oKBtn || this.cancelBtn;
  }

  public doToggleState(event: Event): void {
    if (this.togglingCloseOpen === ToggleState.startclose || this.togglingCloseOpen === ToggleState.justclose) {
      if (this.togglingCloseOpen === ToggleState.justclose) {
        this.togglingCloseOpen = ToggleState.none;
      }
      this.doClose();
    } else if (this.togglingCloseOpen === ToggleState.finishclose) {
      this.togglingCloseOpen = ToggleState.startopen;
      this.isShowing = true;
    }
  }

  private lineIsLink(line: string): boolean {
    return !!line && line.startsWith('$edx_link:');
  }

  private getLinkFromLine(line: string): string {
    if (this.lineIsLink(line)) {
      let link: string = line.substr(10);
      const linkAndTitle: string = link;
      const endOfLink: number = linkAndTitle.indexOf('$edx_link_title:');
      if (endOfLink >= 0) {
        link = link.substr(0, endOfLink);
      }
      return link;
    }
    return null;
  }

  private getLinkTitleFromLine(line: string): string {
    if (this.lineIsLink(line)) {
      const link: string = line.substr(10);
      const endOfLink: number = link.indexOf('$edx_link_title:');
      if (endOfLink >= 0) {
        return link.substr(endOfLink + 16);
      }
      return link;
    }
    return null;
  }

  private openLink(url: string, name: string, event: Event): void {
    Util.Help.openURL(url, name, event);
    setTimeout(() => {
      this.doClose();
    }, 1);
  }

  public doShow(kind: DialogKind, blocking: boolean, cancelBtn: boolean, oKBtn: boolean, title: string, body: any, okText: string, subFormData: any): Promise<any> {
    const prevBlocking = this.blocking;
    this.kind = kind;
    this.title = title;
    this.blocking = blocking;
    this.cancel = kind === DialogKind.progress ? this.localizer.getTranslation('FORMS.BUTTONS.CLOSE') : this.localizer.getTranslation('FORMS.BUTTONS.CANCEL');
    this.cancelBtn = cancelBtn;
    this.oKBtn = oKBtn;
    this.subFormData = subFormData;
    this.subFormName = null;
    this.lines = null;
    this.updateKindText();
    if (body && typeof body === 'string') {
      if (body.startsWith(Util.kLocalFormPrefix)) {
        this.subFormName = body;
      } else {
        this.lines = [];
        const lines: string[] = body.split('\n');
        for (const line of lines) {
          this.lines.push(line);
        }
      }
    }
    if (this.showConfirm()) {
      this.userConfirmed = undefined;
      this.ok = okText ? okText : this.localizer.getTranslation('FORMS.BUTTONS.OK');
    } else {
      this.userConfirmed = true;
    }
    return new Promise<any>((resolve, reject) => {
      const waitFunc = () => {
        this.togglingCloseOpen = ToggleState.none;
        if (this.userConfirmed !== undefined) {
          if (this.kind === DialogKind.success) {
            let timeOut: number = Number(Util.RestAPI.getPreference('success_dialog_limit'));
            if (!timeOut) {
              timeOut = 2;
            }
            timeOut *= 1000;
            setTimeout(() => {
              this.togglingCloseOpen = ToggleState.justclose;
              this.toggleStateBtn.nativeElement.click();
            }, timeOut);
          }
          resolve({ confirm: this.userConfirmed, data: this.subFormWrapper ? this.subFormWrapper.getValue() : {} });
        } else {
          setTimeout(waitFunc, 500);
        }
      };
      const waitToggleFunc = () => {
        if (this.togglingCloseOpen === ToggleState.startopen && this.shown) {
          setTimeout(waitFunc, 1);
        } else {
          setTimeout(waitToggleFunc, 100);
        }
      };
      if (this.shown) {
        this.togglingCloseOpen = ToggleState.startclose;
        this.toggleStateBtn.nativeElement.click();
        if (prevBlocking) {
          this.shortcutService.setDisableShortcuts(false);
        }
        setTimeout(waitToggleFunc, 100);
      } else {
        this.isShowing = true;
        setTimeout(waitFunc, 1);
      }
      if (this.blocking) {
        this.shortcutService.setDisableShortcuts(true);
      }
    });
  }

  public notifyAnimationComplete(): void {
    if (this.isHiding) {
      this.shown = false;
      Util.RestAPI.getAppComponent().dialogClosed();
    } else if (this.isShowing) {
      this.shown = true;
    }
    this.isShowing = false;
    this.isHiding = false;
    if (this.togglingCloseOpen === ToggleState.startclose) {
      this.togglingCloseOpen = ToggleState.finishclose;
      this.toggleStateBtn.nativeElement.click();
    }
    setTimeout(() => {
      if (!!this.dialogHeader) {
        (this.dialogHeader.nativeElement as HTMLElement).focus();
      }
    }, 100);
  }

  public confirmCancel(): void {
    this.userConfirmed = false;
    this.doClose();
  }

  public confirmOK(): void {
    this.userConfirmed = true;
    this.doClose();
  }

  public doClose(event?: Event): void {
    if (this.blocking) {
      this.shortcutService.setDisableShortcuts(false);
    }
    if (this.shown) {
      this.isHiding = true;
    }
    if (this.isShowing) {
      setTimeout(() => {
        if (this.isShowing) {
          // can happen if we abort and opening and the animation callback does not run
          this.isShowing = false;
          if (this.shown) {
            this.isHiding = true;
          }
        }
      }, 1000);
    }
  }

  public showHide(show: boolean): void {
    this.hidden = !show;
  }

  public updateFormData(formData: any): void {
    if (this.shown) {
      this.subFormData = formData;
    }
  }

  public updateTitle(title: string): void {
    this.title = title;
  }

  public updateKindText(): void {
    this.kindText = DialogKind[this.kind].toString();
  }
}
